import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Save, Settings, Check, Star, CreditCard, Eye, EyeOff, Globe, ShieldCheck, Smartphone, KeyRound, AlertTriangle, Download, Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const api = useCallback((method, url, data) =>
    axios({ method, url: `${BACKEND_URL}${url}`, data, headers: { Authorization: `Bearer ${token}` } }),
  [token]);

  const [settings, setSettings] = useState({ monthly_revenue_target: 5000, admin_name: '', google_review_link: '', contact_phone: '', stripe_public_key: '', stripe_secret_key: '', embedsocial_widget_id: '', gite_salle_enabled: true, keybox_code: '', wifi_ssid: '', wifi_password: '', arrival_recommendations: '', billing_company_name: 'E.I LE DOMAINE DU PILAT', billing_exploitant: 'Amar Chemami', billing_address: "4 route du gouffre d'enfer, 42100 Saint-Etienne", billing_siret: '', billing_tva_number: '', billing_iban: '', billing_footer: '' });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    api('get', '/api/admin/settings').then(r => {
      setSettings({
        monthly_revenue_target: r.data.monthly_revenue_target || 5000,
        admin_name: r.data.admin_name || '',
        google_review_link: r.data.google_review_link || '',
        contact_phone: r.data.contact_phone || '',
        stripe_public_key: r.data.stripe_public_key || '',
        stripe_secret_key: r.data.stripe_secret_key || '',
        embedsocial_widget_id: r.data.embedsocial_widget_id || '',
        gite_salle_enabled: r.data.gite_salle_enabled !== false,
        keybox_code: r.data.keybox_code || '',
        wifi_ssid: r.data.wifi_ssid || '',
        wifi_password: r.data.wifi_password || '',
        arrival_recommendations: r.data.arrival_recommendations || '',
        billing_company_name: r.data.billing_company_name || 'E.I LE DOMAINE DU PILAT',
        billing_exploitant: r.data.billing_exploitant || 'Amar Chemami',
        billing_address: r.data.billing_address || "4 route du gouffre d'enfer, 42100 Saint-Etienne",
        billing_siret: r.data.billing_siret || '',
        billing_tva_number: r.data.billing_tva_number || '',
        billing_iban: r.data.billing_iban || '',
        billing_footer: r.data.billing_footer || '',
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token, navigate, api]);

  const save = async () => {
    try {
      await api('put', '/api/admin/settings', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
  };

  const logout = () => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_refresh_token'); localStorage.removeItem('admin_email'); navigate('/admin'); };

  if (loading) return <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center"><p className="text-[#595959]">Chargement...</p></div>;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="settings-back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl flex items-center gap-2">
              <Settings className="w-5 h-5" strokeWidth={1.5} /> Parametres
            </h1>
          </div>
          <Button onClick={logout} variant="ghost" className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-white/10" size="sm">Deconnexion</Button>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 md:px-12 py-8 space-y-6">
        {/* Admin identity */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4">Identite administrateur</h2>
          <div>
            <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Votre prenom (affiche sur le tableau de bord)</label>
            <Input
              data-testid="settings-admin-name"
              value={settings.admin_name}
              onChange={e => setSettings({ ...settings, admin_name: e.target.value })}
              placeholder="Ex: Jean"
              className="max-w-sm rounded-lg border-[#E5E5E0]"
            />
          </div>
        </div>

        {/* Revenue target */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4">Objectifs financiers</h2>
          <div>
            <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Objectif de chiffre d'affaires mensuel (EUR)</label>
            <Input
              data-testid="settings-monthly-target"
              type="number"
              value={settings.monthly_revenue_target}
              onChange={e => setSettings({ ...settings, monthly_revenue_target: parseFloat(e.target.value) || 0 })}
              placeholder="5000"
              className="max-w-[200px] rounded-lg border-[#E5E5E0]"
            />
            <p className="text-xs text-[#595959] mt-1.5">Utilise pour la jauge du tableau de bord. Ajustez selon la saison.</p>
          </div>
        </div>

        {/* Google Reviews */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-[#C4A962]" /> Avis Google</h2>
          <div>
            <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Lien direct vers la page d'avis Google Maps</label>
            <Input
              data-testid="settings-google-link"
              value={settings.google_review_link}
              onChange={e => setSettings({ ...settings, google_review_link: e.target.value })}
              placeholder="https://g.page/r/votre-lien/review"
              className="max-w-lg rounded-lg border-[#E5E5E0]"
            />
            <p className="text-xs text-[#595959] mt-1.5">Ce lien sera inclus dans l'email J+1 envoye aux clients apres leur sejour.</p>
          </div>
        </div>

        {/* Telephone affiche sur le site */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4 flex items-center gap-2"><Phone className="w-5 h-5 text-[#8A9A5B]" /> Telephone affiche sur le site</h2>
          <div>
            <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Numero de telephone public</label>
            <Input
              data-testid="settings-contact-phone"
              value={settings.contact_phone}
              onChange={e => setSettings({ ...settings, contact_phone: e.target.value })}
              placeholder="06 12 34 56 78"
              className="max-w-[280px] rounded-lg border-[#E5E5E0]"
            />
            <p className="text-xs text-[#595959] mt-1.5">Affiche dans le pied de page du site avec un lien d'appel cliquable. <strong>Laissez vide pour afficher &laquo; Sur demande &raquo;</strong> (les visiteurs seront invites a vous contacter par email).</p>
          </div>
        </div>

        {/* Stripe */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4 flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#635BFF]" /> Stripe - Paiements en ligne</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Cle publique (pk_test_... ou pk_live_...)</label>
              <Input
                data-testid="settings-stripe-public"
                value={settings.stripe_public_key}
                onChange={e => setSettings({ ...settings, stripe_public_key: e.target.value })}
                placeholder="pk_test_..."
                className="max-w-lg rounded-lg border-[#E5E5E0] font-mono text-xs"
              />
              <p className="text-xs text-[#595959] mt-1">Visible cote client. Trouvable dans votre <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#635BFF] underline">Dashboard Stripe → Developers → API keys</a></p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Cle secrete (sk_test_... ou sk_live_...)</label>
              <div className="flex gap-2 items-center max-w-lg">
                <Input
                  data-testid="settings-stripe-secret"
                  type={showSecret ? 'text' : 'password'}
                  value={settings.stripe_secret_key}
                  onChange={e => setSettings({ ...settings, stripe_secret_key: e.target.value })}
                  placeholder="sk_test_..."
                  className="flex-1 rounded-lg border-[#E5E5E0] font-mono text-xs"
                />
                <Button variant="ghost" size="sm" onClick={() => setShowSecret(!showSecret)} className="text-[#595959]">
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-[#595959] mt-1">Utilise cote serveur pour creer les sessions de paiement. Ne sera jamais affiche publiquement.</p>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-[#F5F5F0]">
              {settings.stripe_public_key && settings.stripe_secret_key ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Stripe configure</Badge>
              ) : (
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Configuration incomplete</Badge>
              )}
              <span className="text-xs text-[#595959]">
                {settings.stripe_public_key && settings.stripe_secret_key
                  ? `Mode ${settings.stripe_secret_key.startsWith('sk_live') ? 'PRODUCTION' : 'TEST'}`
                  : 'Renseignez vos deux cles pour activer les paiements'}
              </span>
            </div>
          </div>
        </div>

        {/* Feature flags / Formules actives */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="settings-feature-flags">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4">Formules commercialisables</h2>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="settings-gite-salle-toggle"
              checked={settings.gite_salle_enabled !== false}
              onChange={e => setSettings({ ...settings, gite_salle_enabled: e.target.checked })}
              className="mt-1 w-4 h-4 accent-[#8A9A5B]"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-[#2C3E30] block">Gite + Salle des fetes</span>
              <span className="text-xs text-[#595959] block mt-0.5">
                Decochez tant que la salle est en cours d'amenagement. Quand desactivee : la formule disparait du formulaire de reservation, de la page Hebergements et est remplacee par un message "Bientot disponible" dans la section Evenements.
              </span>
              {settings.gite_salle_enabled === false && (
                <span className="inline-block mt-2 text-[11px] bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">
                  Actuellement desactivee
                </span>
              )}
            </div>
          </label>
        </div>

        {/* Billing / Facturation */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="settings-billing">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-1">Facturation</h2>
          <p className="text-xs text-[#595959] mb-4">
            Informations legales imprimees sur les factures generees depuis l&apos;admin. Tant que le
            SIRET n&apos;est pas renseigne, les factures sont en mode <strong>Brouillon</strong>
            (filigrane rouge, envoi email desactive).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Raison sociale</label>
              <Input data-testid="settings-billing-company" value={settings.billing_company_name} onChange={e => setSettings({ ...settings, billing_company_name: e.target.value })} className="rounded-lg border-[#E5E5E0]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Exploitant</label>
              <Input data-testid="settings-billing-exploitant" value={settings.billing_exploitant} onChange={e => setSettings({ ...settings, billing_exploitant: e.target.value })} className="rounded-lg border-[#E5E5E0]" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Adresse</label>
              <Input data-testid="settings-billing-address" value={settings.billing_address} onChange={e => setSettings({ ...settings, billing_address: e.target.value })} className="rounded-lg border-[#E5E5E0]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">
                SIRET <span className="text-red-600">*</span>
              </label>
              <Input data-testid="settings-billing-siret" value={settings.billing_siret} onChange={e => setSettings({ ...settings, billing_siret: e.target.value })} placeholder="14 chiffres" className="rounded-lg border-[#E5E5E0] font-mono" />
              <p className="text-xs text-[#595959] mt-1">Obligatoire pour envoyer une facture par email.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Numero TVA intracommunautaire</label>
              <Input data-testid="settings-billing-tva" value={settings.billing_tva_number} onChange={e => setSettings({ ...settings, billing_tva_number: e.target.value })} placeholder="FRXX XXXXXXXXX" className="rounded-lg border-[#E5E5E0] font-mono" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">IBAN (pour les virements)</label>
              <Input data-testid="settings-billing-iban" value={settings.billing_iban} onChange={e => setSettings({ ...settings, billing_iban: e.target.value })} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" className="rounded-lg border-[#E5E5E0] font-mono" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Mentions de pied de facture (optionnel)</label>
              <textarea data-testid="settings-billing-footer" value={settings.billing_footer} onChange={e => setSettings({ ...settings, billing_footer: e.target.value })} rows={3} placeholder="Ex: Etablissement recevant du public — Merci de votre confiance." className="w-full rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm text-[#2C3E30] focus:outline-none focus:ring-1 focus:ring-[#8A9A5B]" />
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-[#F5F5F0] mt-4">
            {settings.billing_siret ? (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Factures actives</Badge>
            ) : (
              <Badge className="bg-red-50 text-red-700 border-red-200 border">Mode BROUILLON</Badge>
            )}
            <span className="text-xs text-[#595959]">
              {settings.billing_siret
                ? 'Les factures peuvent etre generees et envoyees par email.'
                : "Renseignez votre SIRET pour pouvoir envoyer les factures par email."}
            </span>
          </div>
        </div>

        {/* Arrival info codes (sent automatically at J-7 to lodging-only reservations) */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="settings-arrival-info">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-1">Infos d&apos;arrivee automatiques (J-7)</h2>
          <p className="text-xs text-[#595959] mb-4">
            Ces informations sont envoyees automatiquement par email 7 jours avant l&apos;arrivee
            aux reservations <strong>Apt Terrasse, Apt Panorama et Gite seul</strong>. Les reservations
            <strong> Gite + Salle</strong> et <strong>Salle seule</strong> ne recoivent pas ce mail
            (accueil physique).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Code boite a cles</label>
              <Input
                data-testid="settings-keybox-code"
                value={settings.keybox_code}
                onChange={e => setSettings({ ...settings, keybox_code: e.target.value })}
                placeholder="Ex: 1234"
                className="rounded-lg border-[#E5E5E0] font-mono tracking-widest"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Reseau Wi-Fi (SSID)</label>
              <Input
                data-testid="settings-wifi-ssid"
                value={settings.wifi_ssid}
                onChange={e => setSettings({ ...settings, wifi_ssid: e.target.value })}
                placeholder="Ex: DomainePilat"
                className="rounded-lg border-[#E5E5E0]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Mot de passe Wi-Fi</label>
              <Input
                data-testid="settings-wifi-password"
                value={settings.wifi_password}
                onChange={e => setSettings({ ...settings, wifi_password: e.target.value })}
                placeholder="Mot de passe du reseau"
                className="rounded-lg border-[#E5E5E0] font-mono"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">Recommandations (texte libre)</label>
              <textarea
                data-testid="settings-arrival-recommendations"
                value={settings.arrival_recommendations}
                onChange={e => setSettings({ ...settings, arrival_recommendations: e.target.value })}
                placeholder={'Conseils pratiques pour le voyageur, par exemple :\n- Check-in a partir de 16h\n- Parking prive a gauche de la grange\n- Boulangerie ouverte de 7h a 13h dans le village'}
                rows={6}
                className="w-full rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm text-[#2C3E30] font-mono focus:outline-none focus:ring-1 focus:ring-[#8A9A5B]"
              />
              <p className="text-xs text-[#595959] mt-1.5">
                Les lignes commencant par <code className="bg-[#f5f5f0] px-1 rounded">-</code> apparaitront en liste a puces dans l&apos;email.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-[#F5F5F0] mt-4">
            {(settings.keybox_code || settings.wifi_ssid || settings.wifi_password) ? (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Configure</Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Non configure</Badge>
            )}
            <span className="text-xs text-[#595959]">
              {(settings.keybox_code || settings.wifi_ssid || settings.wifi_password)
                ? "L'email J-7 sera envoye automatiquement avec ces infos."
                : "Renseignez au moins le code BAL et/ou le wifi pour activer l'envoi automatique."}
            </span>
          </div>
        </div>

        {/* EmbedSocial Google Reviews Widget */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-[#4285F4]" /> Widget Avis Google (EmbedSocial)</h2>
          <div>
            <label className="text-sm font-medium text-[#2C3E30] block mb-1.5">ID du widget EmbedSocial (data-ref)</label>
            <Input
              data-testid="settings-embedsocial-id"
              value={settings.embedsocial_widget_id}
              onChange={e => setSettings({ ...settings, embedsocial_widget_id: e.target.value })}
              placeholder="Ex: abc123def456"
              className="max-w-lg rounded-lg border-[#E5E5E0] font-mono text-xs"
            />
            <p className="text-xs text-[#595959] mt-1.5">
              Trouvable dans <a href="https://embedsocial.com/admin/" target="_blank" rel="noopener noreferrer" className="text-[#4285F4] underline">EmbedSocial</a> &rarr; Widgets &rarr; Code d'integration &rarr; valeur de <code className="bg-[#f5f5f0] px-1 rounded">data-ref</code>.
            </p>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-[#F5F5F0] mt-3">
              {settings.embedsocial_widget_id ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Widget configure</Badge>
              ) : (
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Non configure</Badge>
              )}
              <span className="text-xs text-[#595959]">
                {settings.embedsocial_widget_id ? 'Les avis Google s\'affichent sur la page d\'accueil' : 'Renseignez l\'ID pour afficher les avis Google sur votre site'}
              </span>
            </div>
          </div>
        </div>

        {/* Backup — full JSON export */}
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="settings-backup-section">
          <h2 className="font-semibold text-[#2C3E30] text-lg mb-1 flex items-center gap-2">
            <Download className="w-5 h-5 text-[#8A9A5B]" strokeWidth={1.5} /> Sauvegarde complète
          </h2>
          <p className="text-xs text-[#595959] mb-4">
            Télécharge un fichier JSON contenant <strong>toutes les données</strong> (réservations, tarifs, comptabilité, paramètres...). Conservez-le en lieu sûr : il permet de restaurer ou migrer votre site. Recommandé avant toute migration d&apos;hébergeur.
          </p>
          <a href={`${BACKEND_URL}/api/admin/backup/export?token=${token}`} download>
            <Button variant="outline" size="sm" data-testid="settings-backup-export-btn" className="rounded-full text-xs border-[#8A9A5B] text-[#2C3E30] hover:bg-[#8A9A5B]/10">
              <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Exporter la sauvegarde (.json)
            </Button>
          </a>
        </div>

        {/* Security — 2FA (TOTP) */}
        <SecuritySection api={api} />

        {/* Save button */}
        <div className="flex justify-end">
          <Button
            data-testid="settings-save-btn"
            onClick={save}
            className={`rounded-full px-8 py-5 text-base font-medium transition-all ${saved ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-[#8A9A5B] hover:bg-[#7a8a4d]'} text-white`}
          >
            {saved ? <><Check className="w-4 h-4 mr-2" /> Enregistre</> : <><Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Enregistrer</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SecuritySection({ api }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState({ enabled: false, backup_codes_left: 0, activated_at: null });
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // {secret, qr_svg, otpauth_url}
  const [enableCode, setEnableCode] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState(null); // shown once after enable/regen
  const [disableForm, setDisableForm] = useState({ password: '', code: '', backup_code: '', useBackup: false, open: false });
  const [regenForm, setRegenForm] = useState({ code: '', open: false });
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [logoutAllConfirm, setLogoutAllConfirm] = useState(false);
  const [logoutAllBusy, setLogoutAllBusy] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdErr, setPwdErr] = useState('');

  const submitChangePassword = async () => {
    setPwdErr(''); setPwdMsg('');
    if (pwdForm.next !== pwdForm.confirm) { setPwdErr('La confirmation ne correspond pas au nouveau mot de passe.'); return; }
    if (pwdForm.next.length < 10) { setPwdErr('Le nouveau mot de passe doit contenir au moins 10 caracteres.'); return; }
    if (!/\d/.test(pwdForm.next) || !/[a-zA-Z]/.test(pwdForm.next)) { setPwdErr('Le nouveau mot de passe doit contenir au moins une lettre et un chiffre.'); return; }
    setPwdBusy(true);
    try {
      const r = await api('post', '/api/admin/change-password', {
        current_password: pwdForm.current,
        new_password: pwdForm.next,
        confirm_password: pwdForm.confirm,
      });
      if (r.data?.token) localStorage.setItem('admin_token', r.data.token);
      if (r.data?.refresh_token) localStorage.setItem('admin_refresh_token', r.data.refresh_token);
      setPwdForm({ current: '', next: '', confirm: '' });
      setPwdMsg('Mot de passe modifie avec succes. Tous les autres appareils ont ete deconnectes.');
    } catch (e) {
      const d = e.response?.data?.detail;
      setPwdErr(typeof d === 'string' ? d : 'Erreur lors du changement de mot de passe.');
    } finally { setPwdBusy(false); }
  };

  const submitLogoutAll = async () => {
    setLogoutAllBusy(true);
    try {
      await api('post', '/api/admin/logout-all');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_refresh_token');
      localStorage.removeItem('admin_email');
      navigate('/admin');
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Erreur lors de la deconnexion globale.');
      setLogoutAllBusy(false);
      setLogoutAllConfirm(false);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', '/api/admin/2fa/status');
      setStatus(r.data || {});
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { refresh(); }, [refresh]);

  const startSetup = async () => {
    setBusy(true); setMessage('');
    try {
      const r = await api('post', '/api/admin/2fa/setup');
      setSetupData(r.data);
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Erreur lors de la generation du secret.');
    } finally { setBusy(false); }
  };

  const confirmEnable = async () => {
    setBusy(true); setMessage('');
    try {
      const r = await api('post', '/api/admin/2fa/enable', { code: enableCode });
      setNewBackupCodes(r.data.backup_codes || []);
      setSetupData(null);
      setEnableCode('');
      await refresh();
      setMessage('2FA activee ! Enregistrez vos codes de recuperation en lieu sur.');
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Code invalide.');
    } finally { setBusy(false); }
  };

  const cancelSetup = () => { setSetupData(null); setEnableCode(''); };

  const submitDisable = async () => {
    setBusy(true); setMessage('');
    try {
      const payload = { password: disableForm.password };
      if (disableForm.useBackup) payload.backup_code = disableForm.backup_code;
      else payload.code = disableForm.code;
      await api('post', '/api/admin/2fa/disable', payload);
      setDisableForm({ password: '', code: '', backup_code: '', useBackup: false, open: false });
      setNewBackupCodes(null);
      setMessage('2FA desactivee.');
      await refresh();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Erreur lors de la desactivation.');
    } finally { setBusy(false); }
  };

  const submitRegen = async () => {
    setBusy(true); setMessage('');
    try {
      const r = await api('post', '/api/admin/2fa/regenerate-backup-codes', { code: regenForm.code });
      setNewBackupCodes(r.data.backup_codes || []);
      setRegenForm({ code: '', open: false });
      setMessage('Nouveaux codes de recuperation generes. Les anciens sont invalides.');
      await refresh();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Code invalide.');
    } finally { setBusy(false); }
  };

  const copyCodes = () => {
    if (!newBackupCodes) return;
    navigator.clipboard.writeText(newBackupCodes.join('\n'));
    setMessage('Codes copies dans le presse-papiers.');
  };
  const downloadCodes = () => {
    if (!newBackupCodes) return;
    const content = `Le Domaine du Pilat — Codes de recuperation 2FA\nGeneres le ${new Date().toLocaleString('fr-FR')}\n\n${newBackupCodes.join('\n')}\n\nATTENTION : chaque code n'est utilisable qu'une seule fois.`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'codes-recuperation-2fa.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]" data-testid="settings-security-section">
      <h2 className="font-semibold text-[#2C3E30] text-lg mb-1 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#8A9A5B]" strokeWidth={1.5} /> Securite — Authentification a deux facteurs
      </h2>
      <p className="text-xs text-[#595959] mb-4">
        Ajoute un code a 6 chiffres a chaque connexion admin (Google Authenticator, 1Password, Authy...). <strong>Fortement recommande</strong> en production.
      </p>

      {loading ? (
        <p className="text-sm text-[#8A8A85]">Chargement du statut...</p>
      ) : (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-[#F5F5F0] mb-4" data-testid="settings-2fa-status">
          {status.enabled ? (
            <>
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border" data-testid="settings-2fa-badge-on">2FA activee</Badge>
              <span className="text-xs text-[#595959]">
                Depuis le {status.activated_at ? new Date(status.activated_at).toLocaleDateString('fr-FR') : '?'} — <strong>{status.backup_codes_left}</strong> code{status.backup_codes_left > 1 ? 's' : ''} de recuperation restant{status.backup_codes_left > 1 ? 's' : ''}
              </span>
            </>
          ) : (
            <>
              <Badge className="bg-red-50 text-red-700 border-red-200 border" data-testid="settings-2fa-badge-off">2FA inactive</Badge>
              <span className="text-xs text-[#595959]">Votre compte n&apos;est protege que par un mot de passe.</span>
            </>
          )}
        </div>
      )}

      {message && <div className="p-3 rounded-lg bg-sky-50 text-sky-700 text-sm border border-sky-200 mb-4" data-testid="settings-2fa-message">{message}</div>}

      {/* NEW BACKUP CODES DISPLAY (shown once) */}
      {newBackupCodes && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 mb-4" data-testid="settings-2fa-backup-codes">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-amber-900">Enregistrez ces codes maintenant !</p>
              <p className="text-xs text-amber-800 mt-0.5">Ils ne seront plus affiches. Chaque code n&apos;est utilisable qu&apos;une seule fois.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {newBackupCodes.map((c, i) => (
              <div key={i} data-testid={`backup-code-${i}`} className="p-2 rounded bg-white border border-amber-200 text-center tracking-widest">{c}</div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" data-testid="settings-2fa-copy-codes" onClick={copyCodes} className="rounded-full text-xs">Copier</Button>
            <Button size="sm" variant="outline" data-testid="settings-2fa-download-codes" onClick={downloadCodes} className="rounded-full text-xs">Telecharger .txt</Button>
            <Button size="sm" data-testid="settings-2fa-codes-dismiss" onClick={() => setNewBackupCodes(null)} className="rounded-full text-xs bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040] ml-auto">J&apos;ai enregistre</Button>
          </div>
        </div>
      )}

      {/* SETUP FLOW */}
      {!status.enabled && !setupData && (
        <Button data-testid="settings-2fa-setup-btn" onClick={startSetup} disabled={busy} className="rounded-full bg-[#8A9A5B] text-white hover:bg-[#7a8a4d]">
          <Smartphone className="w-4 h-4 mr-2" strokeWidth={1.5} /> Activer la 2FA
        </Button>
      )}

      {setupData && (
        <div className="p-4 rounded-lg border border-[#E5E5E0] bg-[#FAFAF8]" data-testid="settings-2fa-setup-panel">
          <p className="text-sm text-[#2C3E30] mb-3"><strong>Etape 1 :</strong> scannez ce QR code avec Google Authenticator, 1Password ou Authy.</p>
          <div className="flex flex-col md:flex-row gap-4 items-start">
            <div className="w-48 h-48 shrink-0 overflow-hidden [&_svg]:w-full [&_svg]:h-full" data-testid="settings-2fa-qr" dangerouslySetInnerHTML={{ __html: setupData.qr_svg }} />
            <div className="flex-1 space-y-2">
              <p className="text-xs text-[#595959]">Si le scan echoue, saisissez cette cle manuellement :</p>
              <code className="block p-2 rounded bg-white border border-[#E5E5E0] font-mono text-xs break-all" data-testid="settings-2fa-secret">{setupData.secret}</code>
              <p className="text-xs text-[#8A8A85]">Type : <strong>Time-based (TOTP)</strong>, 6 chiffres, 30s.</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#E5E5E0]">
            <p className="text-sm text-[#2C3E30] mb-2"><strong>Etape 2 :</strong> saisissez le code a 6 chiffres genere par l&apos;appli :</p>
            <div className="flex gap-2">
              <Input data-testid="settings-2fa-enable-code" inputMode="numeric" maxLength={6} value={enableCode} onChange={e => setEnableCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" className="max-w-[160px] font-mono text-center text-lg tracking-widest rounded-lg border-[#E5E5E0]" />
              <Button data-testid="settings-2fa-enable-confirm" onClick={confirmEnable} disabled={busy || enableCode.length !== 6} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700">Confirmer et activer</Button>
              <Button variant="ghost" data-testid="settings-2fa-enable-cancel" onClick={cancelSetup} disabled={busy} className="rounded-full text-[#595959]">Annuler</Button>
            </div>
          </div>
        </div>
      )}

      {/* ACTIVE — regenerate + disable */}
      {status.enabled && !newBackupCodes && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" data-testid="settings-2fa-regen-btn" onClick={() => setRegenForm({ ...regenForm, open: !regenForm.open, code: '' })} className="rounded-full text-xs">
            <KeyRound className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Regenerer les codes de recuperation
          </Button>
          <Button variant="outline" size="sm" data-testid="settings-2fa-disable-btn" onClick={() => setDisableForm({ ...disableForm, open: !disableForm.open })} className="rounded-full text-xs text-red-700 border-red-200 hover:bg-red-50">
            Desactiver la 2FA
          </Button>
        </div>
      )}

      {status.enabled && regenForm.open && (
        <div className="mt-4 p-4 rounded-lg border border-[#E5E5E0] bg-[#FAFAF8]" data-testid="settings-2fa-regen-panel">
          <p className="text-sm text-[#2C3E30] mb-2">Saisissez votre code a 6 chiffres actuel pour confirmer la regeneration.</p>
          <div className="flex gap-2">
            <Input data-testid="settings-2fa-regen-code" inputMode="numeric" maxLength={6} value={regenForm.code} onChange={e => setRegenForm({ ...regenForm, code: e.target.value.replace(/\D/g, '') })} placeholder="123456" className="max-w-[160px] font-mono text-center text-lg tracking-widest rounded-lg border-[#E5E5E0]" />
            <Button data-testid="settings-2fa-regen-confirm" onClick={submitRegen} disabled={busy || regenForm.code.length !== 6} className="rounded-full bg-[#8A9A5B] text-white hover:bg-[#7a8a4d]">Regenerer</Button>
            <Button variant="ghost" data-testid="settings-2fa-regen-cancel" onClick={() => setRegenForm({ code: '', open: false })} className="rounded-full text-[#595959]">Annuler</Button>
          </div>
        </div>
      )}

      {status.enabled && disableForm.open && (
        <div className="mt-4 p-4 rounded-lg border border-red-200 bg-red-50/40" data-testid="settings-2fa-disable-panel">
          <p className="text-sm text-red-900 mb-3"><strong>Attention :</strong> desactiver la 2FA reduit la protection de votre compte a un simple mot de passe.</p>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-[#595959] block mb-1">Mot de passe admin</label>
              <Input type="password" data-testid="settings-2fa-disable-password" value={disableForm.password} onChange={e => setDisableForm({ ...disableForm, password: e.target.value })} className="max-w-sm rounded-lg border-[#E5E5E0]" />
            </div>
            {!disableForm.useBackup ? (
              <div>
                <label className="text-xs text-[#595959] block mb-1">Code a 6 chiffres</label>
                <Input inputMode="numeric" maxLength={6} data-testid="settings-2fa-disable-code" value={disableForm.code} onChange={e => setDisableForm({ ...disableForm, code: e.target.value.replace(/\D/g, '') })} placeholder="123456" className="max-w-[160px] font-mono text-center text-lg tracking-widest rounded-lg border-[#E5E5E0]" />
                <button type="button" data-testid="settings-2fa-disable-use-backup" onClick={() => setDisableForm({ ...disableForm, useBackup: true, code: '' })} className="text-xs text-[#8A9A5B] hover:underline mt-1 block">Utiliser un code de recuperation a la place</button>
              </div>
            ) : (
              <div>
                <label className="text-xs text-[#595959] block mb-1">Code de recuperation</label>
                <Input data-testid="settings-2fa-disable-backup" value={disableForm.backup_code} onChange={e => setDisableForm({ ...disableForm, backup_code: e.target.value.toUpperCase() })} placeholder="XXXXX-XXXXX" className="max-w-sm font-mono tracking-widest rounded-lg border-[#E5E5E0]" />
                <button type="button" data-testid="settings-2fa-disable-use-totp" onClick={() => setDisableForm({ ...disableForm, useBackup: false, backup_code: '' })} className="text-xs text-[#8A9A5B] hover:underline mt-1 block">Utiliser mon appli d&apos;authentification a la place</button>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="settings-2fa-disable-confirm" onClick={submitDisable} disabled={busy || !disableForm.password || (!disableForm.useBackup && disableForm.code.length !== 6) || (disableForm.useBackup && disableForm.backup_code.length < 10)} className="rounded-full bg-red-600 text-white hover:bg-red-700">Desactiver la 2FA</Button>
            <Button variant="ghost" data-testid="settings-2fa-disable-cancel" onClick={() => setDisableForm({ password: '', code: '', backup_code: '', useBackup: false, open: false })} className="rounded-full text-[#595959]">Annuler</Button>
          </div>
        </div>
      )}

      {/* CHANGE PASSWORD */}
      <div className="mt-6 pt-5 border-t border-[#E5E5E0]" data-testid="settings-change-password-section">
        <h3 className="font-semibold text-[#2C3E30] text-sm mb-1 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} /> Changer le mot de passe
        </h3>
        <p className="text-xs text-[#595959] mb-3">
          Minimum 10 caracteres, avec au moins une lettre et un chiffre. Apres le changement, <strong>tous les autres appareils seront deconnectes</strong> (votre session actuelle reste active).
        </p>
        {pwdMsg && <div className="p-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 mb-3" data-testid="settings-pwd-success">{pwdMsg}</div>}
        {pwdErr && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 mb-3" data-testid="settings-pwd-error">{pwdErr}</div>}
        <div className="space-y-2 max-w-sm">
          <div>
            <label className="text-xs text-[#595959] block mb-1">Mot de passe actuel</label>
            <Input type="password" autoComplete="current-password" data-testid="settings-pwd-current" value={pwdForm.current} onChange={e => setPwdForm({ ...pwdForm, current: e.target.value })} className="rounded-lg border-[#E5E5E0]" />
          </div>
          <div>
            <label className="text-xs text-[#595959] block mb-1">Nouveau mot de passe</label>
            <Input type="password" autoComplete="new-password" data-testid="settings-pwd-new" value={pwdForm.next} onChange={e => setPwdForm({ ...pwdForm, next: e.target.value })} className="rounded-lg border-[#E5E5E0]" />
          </div>
          <div>
            <label className="text-xs text-[#595959] block mb-1">Confirmer le nouveau mot de passe</label>
            <Input type="password" autoComplete="new-password" data-testid="settings-pwd-confirm" value={pwdForm.confirm} onChange={e => setPwdForm({ ...pwdForm, confirm: e.target.value })} className="rounded-lg border-[#E5E5E0]" />
          </div>
        </div>
        <Button
          data-testid="settings-pwd-submit"
          onClick={submitChangePassword}
          disabled={pwdBusy || !pwdForm.current || !pwdForm.next || !pwdForm.confirm}
          className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040] mt-3"
        >
          {pwdBusy ? 'Modification...' : 'Changer le mot de passe'}
        </Button>
      </div>

      {/* GLOBAL LOGOUT — all devices */}
      <div className="mt-6 pt-5 border-t border-[#E5E5E0]" data-testid="settings-logout-all-section">
        <h3 className="font-semibold text-[#2C3E30] text-sm mb-1 flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} /> Sessions actives
        </h3>
        <p className="text-xs text-[#595959] mb-3">
          Deconnecte immediatement <strong>tous les appareils</strong> connectes (web et mobile). Utile en cas de perte ou de vol d&apos;un appareil. Vous devrez vous reconnecter partout, y compris ici.
        </p>
        {!logoutAllConfirm ? (
          <Button variant="outline" size="sm" data-testid="settings-logout-all-btn" onClick={() => setLogoutAllConfirm(true)} className="rounded-full text-xs text-red-700 border-red-200 hover:bg-red-50">
            Deconnecter tous les appareils
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50/40" data-testid="settings-logout-all-confirm-panel">
            <span className="text-xs text-red-900">Confirmer ? Toutes les sessions seront invalidees, y compris celle-ci.</span>
            <Button size="sm" data-testid="settings-logout-all-confirm" onClick={submitLogoutAll} disabled={logoutAllBusy} className="rounded-full text-xs bg-red-600 text-white hover:bg-red-700">
              {logoutAllBusy ? 'Deconnexion...' : 'Oui, tout deconnecter'}
            </Button>
            <Button variant="ghost" size="sm" data-testid="settings-logout-all-cancel" onClick={() => setLogoutAllConfirm(false)} className="rounded-full text-xs text-[#595959]">Annuler</Button>
          </div>
        )}
      </div>
    </div>
  );
}
